/**
 * <cart-upsell> — kontekstowe rekomendacje w drawerze ("Może jeszcze to?").
 *
 * ŹRÓDŁO DANYCH = wstrzyknięte SERVER-SIDE w cart-upsell.liquid (ZERO fetcha recs):
 *   data-anchor-gtl  = get_the_look kotwicy (ten kolor + komplementarny typ)
 *   data-anchor-rel  = related_products kotwicy (inne kolory — fallback gdy gtl puste)
 *   data-pin-json    = przypięty produkt impulsowy (stringi-czarne)
 *   data-anchor-id   = kotwica = OSTATNI nie-gift produkt w koszyku (liczona w Liquid)
 *   data-cart-ids    = produkty w koszyku (dedup)
 * KOMPOZYCJA slotów (limit 4): [pin impuls] + 2× get_the_look + 1× wildcard
 *   (wildcard = Shopify related „odkrycie", JEDYNY fetch, cache sessionStorage 30 min per anchor+rynek).
 * Higiena: render TYLKO gdy drawer otwarty (lazy); wildcard fetch tylko gdy zmienia się kotwica.
 *
 * Kształt rec: {id, u(url), t(title), p(cena|money str), c(kolor), img(url), d(has_only_default), v:[{id,t(size),a(available)}]}
 */
class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.limit = parseInt(this.dataset.limit || '8', 10);
    this.addLabel = this.dataset.addLabel || 'Dodaj';
    this.loadedFor = null;
  }

  connectedCallback() {
    this.list = this.querySelector('[data-upsell-list]');
    this.qa = this.querySelector('[data-qa]');
    this.qaName = this.querySelector('[data-qa-name]');
    this.qaSizes = this.querySelector('[data-qa-sizes]');
    this.querySelectorAll('[data-qa-close]').forEach((el) => el.addEventListener('click', () => this.closeQuickAdd()));
    this._onQaKey = (e) => {
      if (e.key === 'Escape') this.closeQuickAdd();
    };
    document.addEventListener('keydown', this._onQaKey);

    // Jeden debounced re-render. Dane są w DOM (świeże po Section Rendering), więc 1 render wystarcza.
    this._onChange = () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.render(), 80);
    };
    document.addEventListener('cart:update', this._onChange);
    document.addEventListener('discount:update', this._onChange);
    this._drawerEl = this.closest('cart-drawer-component') || this.closest('dialog');
    if (this._drawerEl) this._drawerEl.addEventListener('dialog:open', this._onChange);

    this.render();
    this._onChange();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this._onChange);
    document.removeEventListener('discount:update', this._onChange);
    document.removeEventListener('keydown', this._onQaKey);
    clearTimeout(this._t);
    if (this._drawerEl && this._onChange) this._drawerEl.removeEventListener('dialog:open', this._onChange);
  }

  // LAZY: na /cart zawsze; w drawerze tylko gdy realnie otwarty (oszczędza wildcard fetch przy zamkniętym).
  isDrawerOpen() {
    if (!this._drawerEl) return true;
    const dlg = this._drawerEl.matches && this._drawerEl.matches('dialog') ? this._drawerEl : this._drawerEl.querySelector('dialog');
    if (dlg) return dlg.open;
    return this._drawerEl.hasAttribute('open') || this.offsetParent !== null;
  }

  readJSON(sel, fallback) {
    try {
      const el = this.querySelector(sel);
      if (!el) return fallback;
      const v = JSON.parse(el.textContent || 'null');
      return v == null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }

  async render() {
    if (!this.isDrawerOpen()) return; // lazy

    const anchorId = String(this.dataset.anchorId || '');
    const cartIds = new Set(String(this.dataset.cartIds || '').split(',').filter(Boolean));
    if (!anchorId) {
      this.hidden = true;
      return;
    }

    const inCart = (p) => cartIds.has(String(p.id));
    const usable = (arr) => (Array.isArray(arr) ? arr : []).filter((p) => p && p.id && !inCart(p));

    const pin = this.readJSON('[data-pin-json]', null);
    let gtl = usable(this.readJSON('[data-anchor-gtl]', []));
    if (gtl.length === 0) gtl = usable(this.readJSON('[data-anchor-rel]', [])); // fallback: inne kolory

    // Wildcard (Shopify related = odkrycie). 1 fetch, cache po anchorId — pobierz tylko gdy kotwica się zmieniła.
    if (anchorId !== this._wildFor) {
      try {
        this._wildCache = await this.getWildcard(anchorId);
      } catch (e) {
        this._wildCache = [];
      }
      this._wildFor = anchorId;
    }
    const wild = usable(this._wildCache || []);

    // KOMPOZYCJA: pin (slot 1) + get_the_look (slot 2-3) + wildcard (slot 4) -> dedup -> limit
    const seen = new Set();
    const out = [];
    const push = (p) => {
      if (!p || seen.has(String(p.id)) || inCart(p)) return;
      seen.add(String(p.id));
      out.push(p);
    };
    const WILD_RESERVE = 2; // min. tyle slotów na „odkrycie" gdy get_the_look jest długie
    if (pin) push(pin); // slot 1: impuls (stringi)
    const gtlCap = Math.max(1, this.limit - out.length - Math.min(WILD_RESERVE, wild.length));
    gtl.slice(0, gtlCap).forEach(push); // get_the_look (komplet, ten kolor) — większość listy
    wild.forEach((p) => {
      if (out.length < this.limit) push(p);
    }); // wildcard „odkrycie" (Shopify related)
    gtl.slice(gtlCap).forEach((p) => {
      if (out.length < this.limit) push(p);
    }); // dopełnij resztą get_the_look jeśli wildcard krótki

    const final = out.slice(0, this.limit);
    if (final.length === 0) {
      this.hidden = true;
      return;
    }

    this._products = {};
    final.forEach((p) => {
      this._products[String(p.id)] = p;
    });
    this.list.innerHTML = final.map((p) => this.itemHtml(p)).join('');
    this.hidden = false;
    this.bindAddButtons();
    this.loadedFor = anchorId;
  }

  // Shopify related jako 1 slot „odkrycie". Cache sessionStorage TTL 30 min, per anchor+rynek.
  async getWildcard(anchorId) {
    const market = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'x';
    const key = `ggwild:${anchorId}:${market}`;
    try {
      const c = sessionStorage.getItem(key);
      if (c) {
        const o = JSON.parse(c);
        if (o && o.t && Date.now() - o.t < 1800000) return o.d || [];
      }
    } catch (e) {
      /* ignore */
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}recommendations/products.json?product_id=${anchorId}&intent=related&limit=8`;
    const data = await fetch(url, { cache: 'no-store' })
      .then((r) => r.json())
      .catch(() => ({}));
    const recs = (data.products || []).filter((p) => p.available).map((p) => this.normalizeShopify(p));
    try {
      sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: recs }));
    } catch (e) {
      /* ignore */
    }
    return recs;
  }

  // produkt z /recommendations -> nasz znormalizowany kształt (jak wstrzyknięte JSON)
  normalizeShopify(p) {
    const raw = (p.featured_image && (p.featured_image.url || p.featured_image)) || (p.images && p.images[0]) || '';
    return {
      id: p.id,
      u: p.url,
      t: p.title,
      p: this.formatMoney(p.price),
      img: raw ? `${raw}${String(raw).includes('?') ? '&' : '?'}width=300` : '',
      d: (p.variants || []).length <= 1,
      // Pełna nazwa wariantu: przy dwóch opcjach (Długość / Rozmiar) samo option1 dawało rozmiary bez długości (18.10.2026).
      v: (p.variants || []).map((v) => ({ id: v.id, t: v.title || v.option1 || '', a: v.available })),
    };
  }

  itemHtml(p) {
    const imgUrl = p.img || '';
    const price = p.p || '';
    const variants = p.v || [];
    let action;
    if (p.d || variants.length <= 1) {
      const v = variants.find((x) => x.a) || variants[0];
      action = `<button class="cart-upsell__add" type="button" data-variant-id="${v ? v.id : ''}" ${v && v.a ? '' : 'disabled'} aria-label="${this.esc(this.addLabel)} ${this.esc(p.t)}">${this.esc(this.addLabel)}</button>`;
    } else {
      action = `<button class="cart-upsell__add" type="button" data-product-id="${this.esc(String(p.id))}" aria-label="${this.esc(this.addLabel)} ${this.esc(p.t)}">${this.esc(this.addLabel)}</button>`;
    }
    return `
      <li class="cart-upsell__item">
        <a class="cart-upsell__media" href="${p.u}" tabindex="-1">
          ${imgUrl ? `<img src="${imgUrl}" alt="${this.esc(p.t)}" loading="lazy">` : ''}
        </a>
        <a class="cart-upsell__name" href="${p.u}">${this.esc(p.t)}</a>
        <span class="cart-upsell__price">${price}</span>
        ${action}
      </li>`;
  }

  bindAddButtons() {
    this.list.querySelectorAll('.cart-upsell__add').forEach((btn) => {
      btn.addEventListener('click', () => this.onAddClick(btn));
    });
  }

  onAddClick(btn) {
    if (btn.dataset.productId) {
      const p = this._products && this._products[btn.dataset.productId];
      if (p) this.openQuickAdd(p);
    } else {
      this.addToCart(btn);
    }
  }

  // Popup wyboru rozmiaru (produkt > 1 wariantu)
  openQuickAdd(p) {
    if (!this.qa || !this.qaSizes) return;
    if (this.qaName) this.qaName.textContent = p.t;
    const variants = p.v || [];
    this.qaSizes.innerHTML = variants
      .map((v) => {
        const lbl = this.esc(v.t || '');
        return `<button class="cart-upsell__qa-size" type="button" data-variant-id="${v.id}" ${v.a ? '' : 'disabled'} aria-label="${this.esc(p.t)} ${lbl}">${lbl}</button>`;
      })
      .join('');
    this.qaSizes.querySelectorAll('.cart-upsell__qa-size').forEach((b) => {
      b.addEventListener('click', () => {
        this.closeQuickAdd();
        this.addToCart(b);
      });
    });
    this.qa.hidden = false;
  }

  closeQuickAdd() {
    if (this.qa) this.qa.hidden = true;
  }

  async addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.classList.add('is-loading');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((c) => sectionIds.add(c.dataset.sectionId));
    const ownSection = this.closest('[id^="shopify-section-"]');
    if (ownSection) sectionIds.add(ownSection.id.replace('shopify-section-', ''));

    try {
      const body = { items: [{ id: variantId, quantity: 1 }] };
      if (sectionIds.size) body.sections = Array.from(sectionIds).join(',');

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      // Re-render TYLKO wnętrza koszyka (nie całej sekcji header) — bez efektu „przeładowania".
      if (data.sections) {
        for (const html of Object.values(data.sections)) {
          if (!html) continue;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const freshInner = doc.querySelector('.cart-drawer__inner');
          const liveInner = document.querySelector('.cart-drawer__inner');
          if (freshInner && liveInner) {
            liveInner.innerHTML = freshInner.innerHTML;
            continue;
          }
          const freshPage = doc.querySelector('.cart-page');
          const livePage = document.querySelector('.cart-page');
          if (freshPage && livePage) livePage.innerHTML = freshPage.innerHTML;
        }
      }

      // Po podmianie innerHTML nowe <cart-upsell> czytają świeże wstrzyknięte dane.
      document.querySelectorAll('cart-upsell').forEach((el) => {
        if (typeof el.render === 'function') {
          el._wildFor = null;
          el.render();
        }
      });

      const cart = await fetch('/cart.js').then((r) => r.json());
      document.querySelectorAll('.cart-bubble, [data-cart-count]').forEach((b) => {
        const countEl = b.querySelector('[data-cart-count]') || b;
        if (countEl) countEl.textContent = cart.item_count;
      });
    } catch (e) {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }

  formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    const amount = (cents / 100).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${amount} ${currency}`.trim();
  }

  esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
